import type { ListBuilderConfiguration } from '../types';

/**
 * List type value mapping
 */
const LIST_TYPE_VALUE_MAPPING = [
	{
		value: 'static',
		label: 'Static',
		color: { bg: '#e5e7eb', text: '#4b5563' },
	},
	{
		value: 'dynamic',
		label: 'Dynamic',
		color: { bg: '#dbeafe', text: '#1e40af' },
	},
];

/**
 * Active status value mapping
 */
const ACTIVE_STATUS_VALUE_MAPPING = [
	{
		value: true,
		label: 'Active',
		color: { bg: '#dcfce7', text: '#166534' },
	},
	{
		value: false,
		label: 'Inactive',
		color: { bg: '#fef3c7', text: '#92400e' },
	},
];

/**
 * Automation Target Lists configuration
 * Converted from JSON to TypeScript for type safety
 */
export const AUTOMATION_TARGET_LISTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-automation-target-lists',
			cacheTTL: 600000,
			name: 'All Target Lists',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'list-name',
					headers: [{ id: 'list-name-header', text: 'Name' }],
					fields: [
						{
							id: 'name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'name',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							link: {
								type: 'object',
								enabled: true,
							},
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'description',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					id: 'list-type',
					headers: [{ id: 'list-type-header', text: 'Type' }],
					fields: [
						{
							id: 'list-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'listType',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: LIST_TYPE_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 100,
				},
				{
					id: 'item-count',
					headers: [{ id: 'item-count-header', text: 'Items' }],
					fields: [
						{
							id: 'active-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemCounts.active',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
							suffix: ' active',
						},
						{
							id: 'line-break-count',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'excluded-count-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'itemCounts.excluded',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
							suffix: ' excluded',
							hideIfZero: true,
						},
					],
					width: 120,
				},
				{
					id: 'status',
					headers: [{ id: 'status-header', text: 'Status' }],
					fields: [
						{
							id: 'status-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'isActive',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								valueMapping: ACTIVE_STATUS_VALUE_MAPPING,
							},
							displayStyle: 'badge',
						},
					],
					sortable: true,
					width: 100,
				},
				{
					id: 'entity-type',
					headers: [{ id: 'entity-type-header', text: 'Entity Type' }],
					fields: [
						{
							id: 'entity-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'entityType',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					width: 120,
				},
				{
					id: 'created-date',
					headers: [{ id: 'created-date-header', text: 'Created' }],
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'createdDate',
									objectTable: 'automation_target_list',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'muted' },
							},
							displayStyle: 'date',
							formatOptions: {
								dateStyle: 'short',
							},
						},
					],
					sortable: true,
					width: 120,
				},
			],
			defaultSortId: 'list-name-asc',
			sortFields: [
				{
					objectTable: '_automation_target_list',
					fieldName: 'name',
					direction: 'ASC',
				},
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50, 100],
	},
	filters: {
		activeFilters: [
			'automationTargetKeywords',
			'automationTargetType',
			'automationTargetCreatedDate',
		],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/automation/target-lists/:id',
			objectLabel: 'Target List',
			objectTable: '_automation_target_list',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'listType', fieldLabel: 'List Type' },
				{ fieldName: 'entityType', fieldLabel: 'Entity Type' },
				{ fieldName: 'isActive', fieldLabel: 'Status' },
				{ fieldName: 'activeCount', fieldLabel: 'Active Count' },
				{ fieldName: 'excludedCount', fieldLabel: 'Excluded Count' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
			],
		},
	],
};
